import moment from 'moment';
import { createCustomComparePeriod, normalizeCompareDateKey, STATISTICS_MAX_CUSTOM_COMPARE_PERIODS } from './statisticsCompareConfig';
import { deriveAnchorMonthIndices, deriveAnchorQuarterIndices, reconcileAnchorPeriodMetaFromRange } from './periodQuickPickStaging';
import { pickStatisticsScopeFields } from './statisticsFilterParams';
import {
	getStatisticsQuickPickYearsFallback,
	STATISTICS_QUICK_PICK_MONTHS,
	STATISTICS_QUICK_PICK_QUARTERS,
	formatMonthIndicesRangeLabel,
	matchRollingPeriodPresetId,
} from './statisticsPeriodQuickPickUtils';

export { normalizeCompareDateKey };

export const TREND_GRANULARITY = {
	YEAR: 'year',
	QUARTER: 'quarter',
	MONTH: 'month',
	RANGE: 'range',
};

/** 对比区间展示名（整月 / 整季 / 全年优先用简短中文） */
export const formatComparePeriodLabel = (startDate, endDate) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid()) return '';

	if (start.isSame(end, 'day')) return start.format('YYYY年M月D日');

	const isFullMonth = start.date() === 1 && start.isSame(end, 'month') && end.date() === end.daysInMonth();
	if (isFullMonth) return start.format('YYYY年M月');

	const quarterStartMonth = Math.floor(start.month() / 3) * 3;
	const isFullQuarter =
		start.date() === 1 &&
		start.month() === quarterStartMonth &&
		end.month() === quarterStartMonth + 2 &&
		end.date() === end.daysInMonth() &&
		start.isSame(end, 'year');
	if (isFullQuarter) return `${start.format('YYYY年')}Q${Math.floor(start.month() / 3) + 1}`;

	const isFullYear =
		start.month() === 0 &&
		start.date() === 1 &&
		end.month() === 11 &&
		end.date() === 31 &&
		start.isSame(end, 'year');
	if (isFullYear) return `${start.format('YYYY年')}`;

	return `${start.format('YYYY-MM-DD')} ~ ${end.format('YYYY-MM-DD')}`;
};

export const detectLineCompareGranularity = (startDate, endDate) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid()) return TREND_GRANULARITY.RANGE;

	if (start.month() === 0 && start.date() === 1 && end.month() === 11 && end.date() === 31 && start.isSame(end, 'year')) {
		return TREND_GRANULARITY.YEAR;
	}

	const quarterStartMonth = Math.floor(start.month() / 3) * 3;
	const isFullQuarter =
		start.date() === 1 &&
		start.month() === quarterStartMonth &&
		end.month() === quarterStartMonth + 2 &&
		end.date() === end.daysInMonth() &&
		start.isSame(end, 'year');
	if (isFullQuarter) return TREND_GRANULARITY.QUARTER;

	if (start.date() === 1 && start.isSame(end, 'month') && end.date() === end.daysInMonth()) {
		return TREND_GRANULARITY.MONTH;
	}

	return TREND_GRANULARITY.RANGE;
};

/** 折线 X 轴：按锚点粒度统一格式化 */
export const formatLineCompareAxisLabel = (startDate, endDate, granularity = TREND_GRANULARITY.RANGE) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid()) return '';

	if (granularity === TREND_GRANULARITY.YEAR) return `${start.format('YYYY年')}`;
	if (granularity === TREND_GRANULARITY.MONTH) return start.format('YYYY年M月');
	if (granularity === TREND_GRANULARITY.QUARTER) {
		return `${start.format('YYYY年')}Q${Math.floor(start.month() / 3) + 1}`;
	}
	return formatComparePeriodLabel(start, end);
};

/** 相对锚点偏移年数：负=前，正=后 */
export const shiftComparePeriodByOffset = (startDate, endDate, offsetYears) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid() || !offsetYears) return null;

	const shiftedStart = offsetYears < 0 ? start.clone().subtract(Math.abs(offsetYears), 'year') : start.clone().add(offsetYears, 'year');
	const shiftedEnd = offsetYears < 0 ? end.clone().subtract(Math.abs(offsetYears), 'year') : end.clone().add(offsetYears, 'year');

	return {
		offsetYears,
		startDate: shiftedStart.format('YYYY-MM-DD'),
		endDate: shiftedEnd.format('YYYY-MM-DD'),
		label: formatComparePeriodLabel(shiftedStart, shiftedEnd),
	};
};

export const comparePeriodsEqual = (left, right) =>
	normalizeCompareDateKey(left?.startDate) === normalizeCompareDateKey(right?.startDate) &&
	normalizeCompareDateKey(left?.endDate) === normalizeCompareDateKey(right?.endDate);

const isYearShiftCompareChip = (item) => item?.offsetYears != null || String(item?.id ?? '').startsWith('shift-');

/** 对比段所属自然年（整年取日历年，否则取开始日年份） */
export const resolveComparePeriodPrimaryYear = (period) => {
	if (!period?.startDate) return null;
	if (detectLineCompareGranularity(period.startDate, period.endDate) === TREND_GRANULARITY.YEAR) {
		const fullYear = moment(period.startDate).year();
		return Number.isFinite(fullYear) ? fullYear : null;
	}
	const year = moment(period.startDate).year();
	return Number.isFinite(year) ? year : null;
};

/** 对比段 tag：基准前 → 基准后，同侧按开始日期升序 */
export const sortCustomComparePeriodsByAnchor = (periods = [], anchorStart, anchorEnd) => {
	if (!anchorStart || !anchorEnd || !periods.length) return [...periods];

	const anchorStartMs = moment(anchorStart).startOf('day').valueOf();
	const dated = [];
	const placeholders = [];

	for (const period of periods) {
		if (!period?.startDate || !period?.endDate) {
			placeholders.push(period);
			continue;
		}
		dated.push(period);
	}

	dated.sort((left, right) => {
		const leftStartMs = moment(left.startDate).startOf('day').valueOf();
		const rightStartMs = moment(right.startDate).startOf('day').valueOf();
		const leftGroup = leftStartMs < anchorStartMs ? 0 : 1;
		const rightGroup = rightStartMs < anchorStartMs ? 0 : 1;
		if (leftGroup !== rightGroup) return leftGroup - rightGroup;
		if (leftStartMs !== rightStartMs) return leftStartMs - rightStartMs;
		return moment(left.endDate).valueOf() - moment(right.endDate).valueOf();
	});

	return [...dated, ...placeholders];
};

export const isComparePeriodSelected = (chip, selected = []) => {
	if (selected.some((item) => comparePeriodsEqual(item, chip))) return true;
	if (!isYearShiftCompareChip(chip)) return false;
	const chipYear = resolveComparePeriodPrimaryYear(chip);
	if (chipYear == null) return false;
	return selected.some((item) => resolveComparePeriodPrimaryYear(item) === chipYear);
};

const MOM_CHIP_OFFSETS = [6, 5, 4, 3, 2, 1];

export const COMPARE_MOM_STEP_UNIT = {
	YEAR: 'year',
	QUARTER: 'quarter',
	MONTH: 'month',
	RANGE: 'range',
};

/** 按季/月对比步进单位：优先对齐选期面板的月/季元数据 */
export const resolveCompareMomStepUnit = (startDate, endDate, anchorMonths = null, anchorQuarters = null) => {
	const quarters = Array.isArray(anchorQuarters) && anchorQuarters.length ? anchorQuarters : null;
	const months = Array.isArray(anchorMonths) && anchorMonths.length ? anchorMonths : null;

	if (quarters?.length) return COMPARE_MOM_STEP_UNIT.QUARTER;
	if (months?.length) return COMPARE_MOM_STEP_UNIT.MONTH;

	const granularity = detectLineCompareGranularity(startDate, endDate);
	if (granularity === TREND_GRANULARITY.QUARTER) return COMPARE_MOM_STEP_UNIT.QUARTER;
	if (granularity === TREND_GRANULARITY.MONTH || granularity === TREND_GRANULARITY.YEAR) {
		return COMPARE_MOM_STEP_UNIT.MONTH;
	}
	return COMPARE_MOM_STEP_UNIT.RANGE;
};

export const resolveCompareMomModeLabels = (stepUnit = COMPARE_MOM_STEP_UNIT.MONTH) => {
	if (stepUnit === COMPARE_MOM_STEP_UNIT.YEAR) {
		return { modeLabel: '按年对比', pickLabel: '按年选择', collapseLabel: '收起按年', emptyHint: '按年' };
	}
	if (stepUnit === COMPARE_MOM_STEP_UNIT.QUARTER) {
		return { modeLabel: '按季对比', pickLabel: '按季选择', collapseLabel: '收起按季', emptyHint: '按季' };
	}
	if (stepUnit === COMPARE_MOM_STEP_UNIT.RANGE) {
		return { modeLabel: '按段对比', pickLabel: '按段选择', collapseLabel: '收起按段', emptyHint: '按段' };
	}
	return { modeLabel: '按月对比', pickLabel: '按月选择', collapseLabel: '收起按月', emptyHint: '按月' };
};

/** 折线按年对比：基准所属年份（整年取该年，否则取开始日年份） */
export const resolveCompareAnchorYear = (startDate, endDate) => {
	if (detectLineCompareGranularity(startDate, endDate) === TREND_GRANULARITY.YEAR) {
		const fullYear = moment(startDate).year();
		if (Number.isFinite(fullYear)) return fullYear;
	}
	const anchorYear = moment(startDate).year();
	return Number.isFinite(anchorYear) ? anchorYear : null;
};

const formatMonthIndicesLabel = formatMonthIndicesRangeLabel;

const formatQuarterIndicesLabel = (quarterIndices = []) =>
	quarterIndices
		.map((quarter) => STATISTICS_QUICK_PICK_QUARTERS.find((item) => item.quarter === quarter)?.label)
		.filter(Boolean)
		.join('、');

const withCompareYearPrefix = (year, text) => {
	if (!text) return '';
	if (!Number.isFinite(Number(year))) return text;
	if (text.startsWith(`${year}年`)) return text;
	return `${year}年 · ${text}`;
};

/** 整年对比段：2024年 · 1~12月 / 2024年 · 各季 */
export const formatFullYearCompareAxisLabel = (year, periodAxis = 'month') => {
	if (periodAxis === 'quarter') {
		const suffix = '各季';
		return Number.isFinite(Number(year)) ? `${Number(year)}年 · ${suffix}` : suffix;
	}
	const monthLabel = formatMonthIndicesRangeLabel(STATISTICS_QUICK_PICK_MONTHS.map(({ month }) => month));
	return Number.isFinite(Number(year)) ? `${Number(year)}年 · ${monthLabel}` : monthLabel;
};

/** 对比 chip / 对比段 tag / 图例：优先按年·月/季展示，不用原始起止日期 */
export const resolveComparePeriodChipLabel = ({
	startDate,
	endDate,
	label,
	periodAxis = null,
	months = null,
	quarters = null,
} = {}) => {
	if (!startDate || !endDate) return label?.trim() || '';

	const year = resolveComparePeriodPrimaryYear({ startDate, endDate });
	let resolvedAxis = periodAxis;
	let resolvedMonths = months?.length ? months : null;
	let resolvedQuarters = quarters?.length ? quarters : null;

	if (resolvedAxis === 'year' && year != null) {
		return `${year}年`;
	}
	if (year != null && label?.trim() === `${year}年`) {
		return `${year}年`;
	}

	if (!resolvedQuarters && !resolvedMonths && Number.isFinite(year)) {
		resolvedQuarters = deriveAnchorQuarterIndices(startDate, endDate, year);
		if (resolvedQuarters?.length) {
			resolvedAxis = resolvedAxis ?? 'quarter';
		} else {
			const inferredMonths = deriveAnchorMonthIndices(startDate, endDate, year);
			if (inferredMonths?.length) {
				resolvedMonths = inferredMonths;
				resolvedAxis = resolvedAxis ?? 'month';
			}
		}
	}

	if (detectLineCompareGranularity(startDate, endDate) === TREND_GRANULARITY.YEAR && year != null) {
		if (resolvedAxis === 'quarter' || resolvedQuarters?.length === STATISTICS_QUICK_PICK_QUARTERS.length) {
			return formatFullYearCompareAxisLabel(year, 'quarter');
		}
		const monthIndices = resolvedMonths ?? STATISTICS_QUICK_PICK_MONTHS.map(({ month }) => month);
		return withCompareYearPrefix(year, formatMonthIndicesLabel(monthIndices));
	}

	if (resolvedAxis === 'quarter' && resolvedQuarters?.length) {
		if (resolvedQuarters.length === STATISTICS_QUICK_PICK_QUARTERS.length) {
			return formatFullYearCompareAxisLabel(year, 'quarter');
		}
		return withCompareYearPrefix(year, formatQuarterIndicesLabel(resolvedQuarters));
	}

	if (resolvedMonths?.length) {
		return withCompareYearPrefix(year, formatMonthIndicesLabel(resolvedMonths));
	}

	const trimmedLabel = label?.trim();
	if (trimmedLabel && trimmedLabel !== formatComparePeriodLabel(startDate, endDate)) {
		return trimmedLabel;
	}

	const friendly = formatComparePeriodLabel(startDate, endDate);
	if (friendly && detectLineCompareGranularity(startDate, endDate) !== TREND_GRANULARITY.RANGE) {
		return friendly;
	}

	return friendly || trimmedLabel || '';
};

const buildCompareYearChipMeta = (startDate, endDate, anchorPeriodAxis, anchorMonths, anchorQuarters, isFullYearAnchor) => {
	const rollingId = matchRollingPeriodPresetId(startDate, endDate);
	if (isFullYearAnchor || rollingId === 'thisYear') {
		return { labelAxis: 'year' };
	}
	if (rollingId === 'thisQuarter' || (anchorPeriodAxis === 'quarter' && anchorQuarters?.length)) {
		const quarters =
			Array.isArray(anchorQuarters) && anchorQuarters.length
				? anchorQuarters
				: deriveAnchorQuarterIndices(startDate, endDate, moment(startDate).year());
		return quarters?.length ? { periodAxis: 'quarter', quarters } : {};
	}
	if (rollingId === 'thisMonth' || (anchorPeriodAxis === 'month' && anchorMonths?.length) || anchorMonths?.length) {
		const months =
			Array.isArray(anchorMonths) && anchorMonths.length
				? anchorMonths
				: deriveAnchorMonthIndices(startDate, endDate, moment(startDate).year());
		return months?.length ? { periodAxis: 'month', months } : {};
	}
	if (anchorPeriodAxis === 'quarter' && anchorQuarters?.length) {
		return { periodAxis: 'quarter', quarters: anchorQuarters };
	}
	return {};
};

const normalizeAvailableYears = (availableYears) => {
	const raw = availableYears?.length ? availableYears : getStatisticsQuickPickYearsFallback();
	return [...new Set(raw.map((year) => Number(year)).filter((year) => Number.isFinite(year)))].sort((a, b) => a - b);
};

/** 按系统可用年份生成基准前/后按年对比 chip */
const buildCompareYearChips = (
	startDate,
	endDate,
	availableYears = [],
	anchorMonths = null,
	anchorQuarters = null,
	anchorPeriodAxis = null,
) => {
	const chipsBefore = [];
	const chipsAfter = [];
	const anchorYear = resolveCompareAnchorYear(startDate, endDate);
	if (anchorYear == null) return { chipsBefore, chipsAfter };

	const years = normalizeAvailableYears(availableYears);
	const isFullYearAnchor = detectLineCompareGranularity(startDate, endDate) === TREND_GRANULARITY.YEAR;
	const chipMeta = buildCompareYearChipMeta(startDate, endDate, anchorPeriodAxis, anchorMonths, anchorQuarters, isFullYearAnchor);
	const { labelAxis, ...storedMeta } = chipMeta;
	const labelMeta = labelAxis === 'year' ? { periodAxis: 'year' } : storedMeta;

	for (const year of years) {
		if (year === anchorYear) continue;

		const offsetYears = year - anchorYear;
		const shifted = shiftComparePeriodByOffset(startDate, endDate, offsetYears);
		if (!shifted) continue;

		const displayLabel = resolveComparePeriodChipLabel({
			startDate: shifted.startDate,
			endDate: shifted.endDate,
			...labelMeta,
		});

		const chip = {
			id: `shift-${offsetYears > 0 ? '+' : ''}${offsetYears}y`,
			offsetYears,
			label: displayLabel,
			startDate: shifted.startDate,
			endDate: shifted.endDate,
			shortLabel: displayLabel,
			...storedMeta,
		};

		if (year < anchorYear) chipsBefore.push(chip);
		else chipsAfter.push(chip);
	}

	return { chipsBefore, chipsAfter };
};

const buildMomChipCursor = (anchorPoint, offset, unit, granularity, direction) => {
	const step = direction === 'before' ? -offset : offset;
	if (unit === 'month' && granularity === TREND_GRANULARITY.YEAR) {
		return anchorPoint.clone().add(step, 'month');
	}
	return anchorPoint.clone().add(step, unit);
};

const normalizeForcedMomStepUnit = (forcedStepUnit = null) => {
	if (forcedStepUnit === COMPARE_MOM_STEP_UNIT.YEAR) return COMPARE_MOM_STEP_UNIT.YEAR;
	if (forcedStepUnit === COMPARE_MOM_STEP_UNIT.QUARTER) return COMPARE_MOM_STEP_UNIT.QUARTER;
	if (forcedStepUnit === COMPARE_MOM_STEP_UNIT.MONTH) return COMPARE_MOM_STEP_UNIT.MONTH;
	if (forcedStepUnit === COMPARE_MOM_STEP_UNIT.RANGE) return COMPARE_MOM_STEP_UNIT.RANGE;
	return null;
};

/** 按年/季/月/段步进 chip；forcedStepUnit 可覆盖选期推导 */
export const buildCompareMomMonthChips = (
	startDate,
	endDate,
	anchorMonths = null,
	anchorQuarters = null,
	forcedStepUnit = null,
) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid()) {
		return { momChipsBefore: [], momChipsAfter: [], momStepUnit: COMPARE_MOM_STEP_UNIT.MONTH };
	}

	const momStepUnit =
		normalizeForcedMomStepUnit(forcedStepUnit) ?? resolveCompareMomStepUnit(startDate, endDate, anchorMonths, anchorQuarters);
	const unit =
		momStepUnit === COMPARE_MOM_STEP_UNIT.YEAR
			? 'year'
			: momStepUnit === COMPARE_MOM_STEP_UNIT.QUARTER
				? 'quarter'
				: 'month';
	let granularity = detectLineCompareGranularity(startDate, endDate);
	if (momStepUnit === COMPARE_MOM_STEP_UNIT.YEAR) {
		granularity = TREND_GRANULARITY.YEAR;
	} else if (momStepUnit === COMPARE_MOM_STEP_UNIT.QUARTER) {
		granularity = TREND_GRANULARITY.QUARTER;
	} else if (momStepUnit === COMPARE_MOM_STEP_UNIT.MONTH && granularity === TREND_GRANULARITY.YEAR) {
		granularity = TREND_GRANULARITY.YEAR;
	}

	const anchorStart = start.clone().startOf('day');
	const anchorEnd = end.clone().startOf('day');
	const today = moment().startOf('day');
	const momChipsBefore = [];
	const momChipsAfter = [];

	for (const offset of MOM_CHIP_OFFSETS) {
		const beforeCursor = buildMomChipCursor(anchorStart, offset, unit, granularity, 'before');
		const beforeStart = beforeCursor.clone().startOf(unit);
		const beforeEnd = beforeCursor.clone().endOf(unit);
		const beforeLabel = formatComparePeriodLabel(beforeStart, beforeEnd);
		momChipsBefore.push({
			id: `mom-${unit}-${beforeStart.format('YYYY-MM-DD')}`,
			label: beforeLabel,
			startDate: beforeStart.format('YYYY-MM-DD'),
			endDate: beforeEnd.format('YYYY-MM-DD'),
			shortLabel: beforeLabel,
		});

		const afterCursor = buildMomChipCursor(anchorEnd, offset, unit, granularity, 'after');
		const afterStart = afterCursor.clone().startOf(unit);
		if (afterStart.isAfter(today, 'day')) continue;
		const afterEnd = afterCursor.clone().endOf(unit);
		const afterLabel = formatComparePeriodLabel(afterStart, afterEnd);
		momChipsAfter.push({
			id: `mom-${unit}-${afterStart.format('YYYY-MM-DD')}`,
			label: afterLabel,
			startDate: afterStart.format('YYYY-MM-DD'),
			endDate: afterEnd.format('YYYY-MM-DD'),
			shortLabel: afterLabel,
		});
	}

	return { momChipsBefore, momChipsAfter, momStepUnit };
};

/** 根据锚点区间生成可点选的对比预设（前/后按系统可用年份） */
export const buildCompareQuickPresets = (
	startDate,
	endDate,
	anchorMonths = null,
	anchorQuarters = null,
	availableYears = [],
	anchorPeriodAxis = null,
	forcedMomStepUnit = null,
) => {
	const start = moment(startDate);
	const end = moment(endDate);
	if (!start.isValid() || !end.isValid()) {
		return { chipsBefore: [], chipsAfter: [], momChipsBefore: [], momChipsAfter: [], momStepUnit: COMPARE_MOM_STEP_UNIT.MONTH, compareModes: [] };
	}

	const { chipsBefore, chipsAfter } = buildCompareYearChips(
		startDate,
		endDate,
		availableYears,
		anchorMonths,
		anchorQuarters,
		anchorPeriodAxis,
	);

	const { momChipsBefore, momChipsAfter, momStepUnit } = buildCompareMomMonthChips(
		startDate,
		endDate,
		anchorMonths,
		anchorQuarters,
		forcedMomStepUnit,
	);

	return {
		chipsBefore,
		chipsAfter,
		momChipsBefore,
		momChipsAfter,
		momStepUnit,
		compareModes: buildCompareActionBundles(momStepUnit),
	};
};

export const addComparePeriods = (existing = [], toAdd = [], max = STATISTICS_MAX_CUSTOM_COMPARE_PERIODS) => {
	let next = [...existing];
	for (const item of toAdd) {
		if (next.length >= max) break;
		if (!item?.startDate || !item?.endDate) continue;
		if (isYearShiftCompareChip(item)) {
			const itemYear = resolveComparePeriodPrimaryYear(item);
			if (itemYear != null) {
				next = next.filter((period) => resolveComparePeriodPrimaryYear(period) !== itemYear);
			}
		}
		if (next.some((period) => comparePeriodsEqual(period, item))) continue;
		next.push(createCustomComparePeriod(item));
	}
	return next;
};

export const removeComparePeriod = (existing = [], period) => {
	if (isYearShiftCompareChip(period)) {
		const chipYear = resolveComparePeriodPrimaryYear(period);
		if (chipYear != null) {
			return existing.filter((item) => resolveComparePeriodPrimaryYear(item) !== chipYear);
		}
	}
	return existing.filter((item) => !comparePeriodsEqual(item, period));
};

export const toggleComparePeriod = (existing = [], period, max = STATISTICS_MAX_CUSTOM_COMPARE_PERIODS) => {
	if (isComparePeriodSelected(period, existing)) {
		return existing;
	}
	return addComparePeriods(existing, [period], max);
};

export const buildSymmetricComparePeriods = (startDate, endDate, count = 2) => {
	const offsets = [];
	for (let n = count; n >= 1; n -= 1) offsets.push(-n);
	for (let n = 1; n <= count; n += 1) offsets.push(n);
	return offsets.map((offset) => shiftComparePeriodByOffset(startDate, endDate, offset)).filter(Boolean);
};

export const buildBundleComparePeriods = (startDate, endDate, bundle) => {
	if (bundle.type === 'symmetric') {
		return buildSymmetricComparePeriods(startDate, endDate, bundle.count ?? 2);
	}
	if (bundle.type === 'yoy' && Array.isArray(bundle.years)) {
		return bundle.years.map((offset) => shiftComparePeriodByOffset(startDate, endDate, -offset)).filter(Boolean);
	}
	return [];
};

/** 与锚点月/季粒度对齐时，为对称偏移区间附带相同月/季下标 */
export const attachAnchorMetaToComparePeriods = (periods = [], anchorMonths, anchorQuarters, anchorPeriodAxis = null) => {
	const periodAxis = anchorPeriodAxis === 'quarter' ? 'quarter' : anchorPeriodAxis === 'month' ? 'month' : null;
	const months = Array.isArray(anchorMonths) && anchorMonths.length ? anchorMonths : undefined;
	const quarters = Array.isArray(anchorQuarters) && anchorQuarters.length ? anchorQuarters : undefined;
	if (!months && !quarters && !periodAxis) return periods;

	const meta =
		periodAxis === 'quarter' && quarters
			? { periodAxis: 'quarter', quarters }
			: months
				? { periodAxis: periodAxis ?? 'month', months }
				: periodAxis
					? { periodAxis }
					: {};
	return periods.map((period) => ({ ...period, ...meta }));
};

export const comparePeriodSetsEqual = (left = [], right = []) => {
	const leftKeys = getCustomComparePeriodDateKeys(left);
	const rightKeys = getCustomComparePeriodDateKeys(right);
	return leftKeys.length > 0 && leftKeys.length === rightKeys.length && leftKeys.every((key, index) => key === rightKeys[index]);
};

export const getCustomComparePeriodDateKeys = (periods = []) =>
	periods
		.filter((item) => item?.startDate && item?.endDate)
		.map((item) => `${normalizeCompareDateKey(item.startDate)}|${normalizeCompareDateKey(item.endDate)}`)
		.sort();

/** 将顶栏全部筛选条件同步到折线对比上下文（含基准日期） */
export const buildTrendSyncPatchFromTopBar = (topBarFilters = {}) => {
	const scope = pickStatisticsScopeFields(topBarFilters);
	const anchor = buildLineCompareAnchorFromOrderRange(scope.startDate, scope.endDate, topBarFilters.trendAnchor);
	return {
		...scope,
		...(anchor ? { trendAnchor: anchor } : {}),
	};
};

const applyLineCompareAnchorPeriodMeta = (anchor, { periodAxis, months, quarters, dualFullPeriod, periods, years, yearPeriods, chartAxisFromOrderRange } = {}) => {
	if (!anchor) return null;
	const nextAnchor = { ...anchor };
	if (periodAxis === 'quarter' || periodAxis === 'month') {
		nextAnchor.periodAxis = periodAxis;
	}
	if (months?.length) nextAnchor.months = months;
	if (quarters?.length) nextAnchor.quarters = quarters;
	if (Array.isArray(periods) && periods.length > 1) {
		nextAnchor.periods = periods;
	} else {
		delete nextAnchor.periods;
	}
	if (years?.length) nextAnchor.years = years;
	else if (!nextAnchor.periods?.length) delete nextAnchor.years;
	if (yearPeriods && typeof yearPeriods === 'object' && Object.keys(yearPeriods).length > 0) {
		nextAnchor.yearPeriods = yearPeriods;
	} else {
		delete nextAnchor.yearPeriods;
	}
	if (chartAxisFromOrderRange) {
		nextAnchor.chartAxisFromOrderRange = chartAxisFromOrderRange;
	} else {
		delete nextAnchor.chartAxisFromOrderRange;
	}
	if (dualFullPeriod) nextAnchor.dualFullPeriod = true;
	if (nextAnchor.periods?.length > 1) {
		return nextAnchor;
	}
	if (nextAnchor.periodAxis === 'quarter' && nextAnchor.quarters?.length && !nextAnchor.dualFullPeriod) {
		delete nextAnchor.months;
	} else if (nextAnchor.periodAxis === 'month' && nextAnchor.months?.length && !nextAnchor.dualFullPeriod) {
		delete nextAnchor.quarters;
	}
	return nextAnchor;
};

/** 选期面板确认 → 折线基准（含按月/按季粒度） */
export const buildLineCompareAnchorFromQuickPickMerged = (merged) => {
	if (!merged?.startDate || !merged?.endDate) return null;
	const normalizedStart = normalizeCompareDateKey(merged.startDate);
	const normalizedEnd = normalizeCompareDateKey(merged.endDate);
	if (!normalizedStart || !normalizedEnd) return null;
	return applyLineCompareAnchorPeriodMeta(
		{
			startDate: normalizedStart,
			endDate: normalizedEnd,
			label: merged.label?.trim() || formatComparePeriodLabel(normalizedStart, normalizedEnd),
		},
		merged,
	);
};

export const buildLineCompareAnchorFromOrderRange = (startDate, endDate, existingAnchor = null) => {
	if (!startDate || !endDate) return null;
	const normalizedStart = normalizeCompareDateKey(startDate);
	const normalizedEnd = normalizeCompareDateKey(endDate);
	if (!normalizedStart || !normalizedEnd) return null;
	const baseAnchor = {
		startDate: normalizedStart,
		endDate: normalizedEnd,
		label: '',
	};
	const merged =
		!existingAnchor || !trendAnchorMatchesOrderRange(existingAnchor, normalizedStart, normalizedEnd)
			? baseAnchor
			: (applyLineCompareAnchorPeriodMeta(baseAnchor, existingAnchor) ?? baseAnchor);
	const anchorYear = moment(normalizedStart).year();
	const reconciled = Number.isFinite(anchorYear)
		? reconcileAnchorPeriodMetaFromRange({
				startDate: normalizedStart,
				endDate: normalizedEnd,
				months: merged.months,
				quarters: merged.quarters,
				periodAxis: merged.periodAxis,
				label: merged.label,
			})
		: merged;
	const nextAnchor = {
		...merged,
		...reconciled,
		label:
			resolveComparePeriodChipLabel({ ...merged, ...reconciled, label: '' }) ||
			formatComparePeriodLabel(normalizedStart, normalizedEnd),
	};
	return nextAnchor;
};

export const trendAnchorMatchesOrderRange = (anchor, startDate, endDate) => {
	if (!anchor?.startDate || !anchor?.endDate || !startDate || !endDate) return false;
	return (
		normalizeCompareDateKey(anchor.startDate) === normalizeCompareDateKey(startDate) &&
		normalizeCompareDateKey(anchor.endDate) === normalizeCompareDateKey(endDate)
	);
};

/** 对比方式切换（不预填段数，由用户在 chip 中自选） */
export const buildCompareActionBundles = (momStepUnit = COMPARE_MOM_STEP_UNIT.MONTH) => {
	const { modeLabel } = resolveCompareMomModeLabels(momStepUnit);
	return [
		{ id: 'compare-mode-year', label: '按年对比', mode: 'year' },
		{ id: 'compare-mode-month', label: modeLabel, mode: 'month' },
	];
};
